/**
 * Manages player health and lives.
 *
 * Emits events through a Phaser EventEmitter so that the HUD
 * and other systems can react to health changes, life loss, and game over.
 *
 * Heart Potion integration: when a life would be lost, the system checks
 * for owned Heart Potions via ShopManager. If available, a potion is consumed
 * to restore the heart and refill health, preventing the life loss.
 */

import {
  MAX_HEALTH,
  HEALTH_DRAIN_PER_MOVE,
  INITIAL_LIVES,
} from '../config/game-config.js';
import { Events } from '../utils/constants.js';
import { isItemActive, consumeItem, getItemCount } from '../systems/ShopManager.js';
import { SHOP_ITEM_HEART_POTION, SHOP_ITEM_TEAR_OF_THREIA } from '../config/game-config.js';
import { getDifficultyMultiplier } from '../utils/audio-config.js';

export default class HealthSystem {
  /**
   * @param {Phaser.Events.EventEmitter} emitter - Shared event bus.
   * @param {number} [health=MAX_HEALTH]
   * @param {number} [lives=INITIAL_LIVES]
   */
  constructor(emitter, health = MAX_HEALTH, lives = INITIAL_LIVES) {
    /** @type {Phaser.Events.EventEmitter} */
    this.emitter = emitter;

    /** @type {number} */
    this.health = health;

    /** @type {number} */
    this.maxHealth = MAX_HEALTH;

    /** @type {number} */
    this.lives = lives;

    /**
     * Re-entrancy guard: prevents _loseLife() from being called multiple
     * times in the same frame (e.g. poison tick + lava idle drain both
     * hitting health=0 simultaneously).
     * @type {boolean}
     * @private
     */
    this._losingLife = false;
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Drain health when the player moves one tile.
   * If health reaches zero, a life is lost.
   *
   * The final drain is: base × multiplier × difficulty.
   * - `multiplier` includes hazard scaling (water/lava) and Heart Pendant reduction.
   * - `difficulty` comes from the Config scene (Easy 0.4×, Normal 0.75×, Hard 1×).
   *
   * @param {number} [multiplier=1] - Drain multiplier (e.g. 2 for water tiles).
   */
  drainOnMove(multiplier = 1) {
    const drain = HEALTH_DRAIN_PER_MOVE * multiplier * getDifficultyMultiplier();
    this.health = Math.max(0, this.health - drain);
    this.emitter.emit(Events.HEALTH_CHANGED, this.health, this.maxHealth);

    if (this.health <= 0) {
      this._loseLife();
    }
  }

  /**
   * Apply a flat amount of damage to the player (e.g. bat attack).
   * Unlike `drainOnMove`, this is NOT scaled by difficulty or HEALTH_DRAIN_PER_MOVE.
   *
   * @param {number} amount - Raw HP to subtract.
   */
  takeDamage(amount) {
    this.health = Math.max(0, this.health - amount);
    this.emitter.emit(Events.HEALTH_CHANGED, this.health, this.maxHealth);

    if (this.health <= 0) {
      this._loseLife();
    }
  }

  /**
   * Heal the player (e.g. from collecting food).
   *
   * @param {number} amount
   */
  heal(amount) {
    this.health = Math.min(this.maxHealth, this.health + amount);
    this.emitter.emit(Events.HEALTH_CHANGED, this.health, this.maxHealth);
  }

  /**
   * Reset health to full (e.g. on new level or respawn).
   */
  resetHealth() {
    this.health = this.maxHealth;
    this.emitter.emit(Events.HEALTH_CHANGED, this.health, this.maxHealth);
  }

  /**
   * Restore health to maximum without affecting lives (e.g. Spirit Star loot).
   * Functionally identical to resetHealth but semantically distinct —
   * used for in-game healing effects rather than level transitions.
   */
  fullHeal() {
    this.health = this.maxHealth;
    this.emitter.emit(Events.HEALTH_CHANGED, this.health, this.maxHealth);
  }

  /**
   * Restore one lost life (e.g. from collecting enough coins).
   * Lives are capped at INITIAL_LIVES (3) — this only restores,
   * never exceeds the starting amount.
   * Emits LIFE_GAINED so the HUD can update.
   *
   * @returns {boolean} Whether a life was actually restored.
   */
  addLife() {
    if (this.lives >= INITIAL_LIVES) {
      return false;
    }
    this.lives += 1;
    this.emitter.emit(Events.LIFE_GAINED, this.lives);
    return true;
  }

  /**
   * Get a snapshot of the current state for saving.
   *
   * @returns {{ health: number, lives: number }}
   */
  getState() {
    return {
      health: this.health,
      lives: this.lives,
    };
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private */
  _loseLife() {
    // Re-entrancy guard: if we're already processing a life loss (e.g.
    // poison tick + lava idle drain both hitting zero in the same frame),
    // skip the duplicate call.
    if (this._losingLife) return;
    this._losingLife = true;

    try {
      // Heart Potion intercept: if the player owns a Heart Potion,
      // consume one to prevent the life loss and restore full health.
      if (isItemActive(SHOP_ITEM_HEART_POTION)) {
        consumeItem(SHOP_ITEM_HEART_POTION);
        this.resetHealth();
        const remaining = getItemCount(SHOP_ITEM_HEART_POTION);
        this.emitter.emit(Events.HEART_POTION_USED, remaining);
        this.emitter.emit(Events.HEART_POTION_CHANGED, remaining);
        return;
      }

      this.lives -= 1;
      this.emitter.emit(Events.LIFE_LOST, this.lives);

      if (this.lives <= 0) {
        // Tear of Threia intercept: if the player owns a Tear, consume it
        // to prevent game over — restore all lives and full health.
        if (isItemActive(SHOP_ITEM_TEAR_OF_THREIA)) {
          consumeItem(SHOP_ITEM_TEAR_OF_THREIA);
          this.lives = INITIAL_LIVES;
          this.resetHealth();
          this.emitter.emit(Events.TEAR_OF_THREIA_USED);
          return;
        }
        this.emitter.emit(Events.GAME_OVER);
      } else {
        // Refill health for the next life
        this.resetHealth();
      }
    } finally {
      this._losingLife = false;
    }
  }
}
